import { CommonDialogs, ImageTable } from "@mcleod/common";
import {
    BlurEvent, ChangeEvent, ClickEvent, Component, CrudDecorator, DataHeader, DataSourceAction,
    DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DataSourceValidationEvent, DropdownItem,
    DropdownSelectionEvent, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, PrintableEvent, Snackbar, Textbox
} from "@mcleod/components";
import {
    Api, Currency, CurrencySettings, CurrencyUtil, DateUtil, HorizontalAlignment, LicenseTester, Model, ModelRow,
    Navigation, NumberUtil, PermissionsUtil, UrlUtil
} from "@mcleod/core";
import { getDetentionControlBoolean, getPayrollControlValue } from "@mcleod/general";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { AutogenLayoutDrsPayee } from "./autogen/AutogenLayoutDrsPayee";
import { AutoMatchProfile } from "@mcleod/dispatch/src/AutoMatchProfile";
import { AllocType } from "./SegAllocDetailTable";
import { AutogenLayoutJobClassLookup } from "./autogen/AutogenLayoutJobClassLookup";
import { CustomerFscQuickInfo } from "@mcleod/ar";
import { LeaveHistory } from "./LeaveHistory";
import { Payee401kRates } from "./Payee401kRates";
import { OfficePayee } from "./officepayroll/OfficePayee";
import { getSegAllocControlBoolean } from "./models/ModelSegAllocControl";

// Payment Methods
const dropdownCheck = new DropdownItem("C", "Check");
const dropdownDirectDeposit = new DropdownItem("D", "Direct Deposit");
const dropdownWire = new DropdownItem("W", "Wire");
const dropdownFuelCard = new DropdownItem("F", "Fuel Card");
const dropdownFactoringCompany = new DropdownItem("5", "Factoring Company");
const dropdownTriumphPay = new DropdownItem("U", "Triumph");
const dropdownContractTransfer = new DropdownItem("T", "Contract Transfer");
const basePaymentMethods = [dropdownCheck, dropdownDirectDeposit, dropdownWire, dropdownFuelCard];
export const allPaymentMethods = [dropdownCheck, dropdownDirectDeposit, dropdownWire, dropdownFuelCard,
    dropdownContractTransfer, dropdownFactoringCompany, dropdownTriumphPay];

// Pay Methods
const dropdownDistance = new DropdownItem("M", "Distance");
const dropdownPercentageOfRevenue = new DropdownItem("P", "Percentage of Revenue");
const dropdownHourly = new DropdownItem("H", "Hourly");
const dropdownFlat = new DropdownItem("F", "Flat");
const dropdownSalary = new DropdownItem("S", "Salary");
const carrierPayMethods = [dropdownFlat, dropdownDistance, dropdownPercentageOfRevenue, dropdownHourly];
const agentPayMethods = [dropdownDistance, dropdownPercentageOfRevenue, dropdownHourly];
const companyDriverPayMethods = [dropdownDistance, dropdownPercentageOfRevenue, dropdownHourly, dropdownSalary];
const ownerPayMethods = [dropdownDistance, dropdownPercentageOfRevenue];
const viewDrsPayeeUrl = "lme/settlement/DrsPayee?mode=update&key=";

const DIRECT_DEPOSIT = "D";
const COMPANY_DRIVER = "C";
const OWNER = "O";
const AGENT = "A";
const CARRIER = "R";

const TAX_INCOME = "tax_table_income";
const TAX_UNEMPLOYMENT = "tax_table_unemployment";


const ACCRUES_BEGIN_YEAR = "B";
const ACCRUES_ANNIVERSARY = "A";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "payee",
    screenName: "Payee",
    navigateToUrl: viewDrsPayeeUrl
};

export class DrsPayee extends AutogenLayoutDrsPayee {
    codeGenerationIsActive: boolean;
    allowManualEntry: boolean;
    private static viewPayeeUrl = "lme/settlement/DrsPayee?mode=update&key=";
    private isMultiCurrencyEnabled: boolean;
    private hasOffice401kRecord: boolean;
    private setOfficeEmployee: boolean;
    private settlementCheckboxOriginalValue: boolean;
    private officePayeeLayout: OfficePayee;
    private showSaveAndCloseOnDataHeader: boolean;

    private currencyTextBoxes: Textbox[];

    override async onLoad() {
        this.isMultiCurrencyEnabled = CurrencySettings.getSingleton()?.multiCurrencyEnabled();
        this.tabDetention.visible = getDetentionControlBoolean("is_active", false);
        this.layout401kRates.mainDataSource.removeValidationListener(this.layout401kRates.validationListener);
        this.layout401kRates.textboxId.required = false;
        this.layoutDirectDeposit.useMethodSetup();

        this.layoutSegAllocDetail.allocType = AllocType.SETTLEMENTPAYEE;

        this.layoutDirectDeposit.tableDirectDeposit.addContentsChangedListener(() => {
            this.setDirectDepositPercentageCount();
        });
        this.layoutDirectDeposit.tableDirectDeposit.addRowCreateListener(() => {
            this.setDirectDepositPercentageCount();
        });

        this.layoutTableLeaveSick.mainDataSource.orderBy = [
            { field: "check_date", sort: "desc" },
            { field: "payee_id", sort: "asc" },
            { field: "applies_to", sort: "asc" },
            { field: "id", sort: "desc" }
        ];
        this.layoutTableLeaveVacation.mainDataSource.orderBy = [
            { field: "check_date", sort: "desc" },
            { field: "payee_id", sort: "asc" },
            { field: "applies_to", sort: "asc" },
            { field: "id", sort: "desc" }
        ];
        this.layoutTableLeaveSick.tableLeave.removeColumn("columnHeaderType");
        this.layoutTableLeaveVacation.tableLeave.removeColumn("columnHeaderType");
        this.currencyTextBoxes = [
            this.textboxQuickpayMinChg,
            this.textboxDetentionMaxPayAmt,
            this.textboxDependents,
            this.textboxOtherIncome,
            this.textboxFedAmtToWhold,
            this.textboxAddFedToWh,
            this.textboxOtherDeductions,
            this.textboxExtraWithholdings,
            this.textboxStAmtToWhold,
            this.textboxAddlStToWhold,
            this.textboxCountyAmtToWhold,
            this.textboxCityAmtToWhold,
            this.textboxTaxableOwed,
            this.textboxDrsPayeepuPayAmount,
            this.textboxDrsPayeesoPayAmount,
            this.textboxDrsPayeedetentionTmMaxPayAmt,
            this.textboxDrsPayeeminimumPay
        ];

        this.addLayoutLoadListener(() => {
            this.currencyTextBoxes.forEach(textbox => {
                textbox.addBlurListener((event: BlurEvent) => {
                    const textbox = event.target as Textbox;
                    this.updateCurrencyOnBlur(textbox.field);
                });
            });
        });

        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return;
            }
            this.addJobClassificationValidation(event);
            if (!this.activeRow.getBoolean("office_employee", false) && !this.activeRow.getBoolean("non_office_emp", false)) {
                event.validationResults.push({
                    isValid: false,
                    component: this.checkboxNonOfficeEmp,
                    validationMessage: "Payee record must be marked for Office and/or Settlement.",
                    caption: "Payee record must be marked for Office and/or Settlement."
                });
            }
        });

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (!(await DuplicateId.validateBeforePost(this.textboxCode, DUPLICATE_VALIDATION_PROPS))) {
                return false;
            }

            if (this.isMultiCurrencyEnabled) {
                if (this.mainDataSource.mode != DataSourceMode.ADD && this.activeRow.get("currency") != this.activeRow.originalData?.["currency"]) {
                    const result = await CommonDialogs.showYesNo(
                        "It is strongly recommended that you consult with your McLeod Software account manager " +
                        "before making changes to the Currency of the Payee.\n\n" +
                        "Changing the Transaction Currency of the Payee will cause unintended results in all " +
                        "money fields for this payee throughout the system. The existing values already stored " +
                        "will not be re-calculated causing out of balance issues for screens, reports, journals, etc.\n\n" +
                        "Proceed with update of this field?"
                    );

                    if (!result) {
                        return false;
                    }
                }
            }

            if (DataSourceMode.SEARCH !== this.mainDataSource.mode && this.activeRow.get("payment_method") == "D") {
                if (!this.layoutDirectDeposit.validateDirectDepositPercentageDistribution(this.textboxPayeeachBatchClassCode, this)) {
                    return false;
                }

                return await this.validateTchChildCarrier();
            }

            for (const row of this.layout401kRates.tablePayee401kRate.rows) {
                const rateDetailsLayout = this.layout401kRates.rateDetailsMap.get(row.data.get("unique_id"));
                if (rateDetailsLayout) {
                    rateDetailsLayout.table401kDetails.dataSource.parentDataSource = this.mainDataSource;
                }
            }

            if (!this.mainDataSource.validate()) {
                return false;
            }
            if (DataSourceMode.SEARCH !== this.mainDataSource.mode && this.activeRow.getChangedData()["office_employee"] === "Y") {
                this.officePayeeLayout = Layout.getLayout("lme/settlement/officepayroll/OfficePayee") as OfficePayee;
                await this.officePayeeLayout.ensureLoaded();
                if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                    await this.officePayeeLayout.mainDataSource.search({
                        id: this.activeRow.get("id"),
                        ignore_office_flag: true
                    }, null, this.officePayeeLayout.layoutName);
                }
                if (this.officePayeeLayout.validate().filter(result => !result.isValid).length > 0) {
                    const isYes = await CommonDialogs.showYesNo("To save this payee as an office payee, required fields on the Office Payee screen must be completed. Would you like to update them now?", "Office Payee");
                    if (isYes) {
                        this.setOfficeEmployee = true;
                    }
                    this.activeRow.set("office_employee", "N");
                    this.settlementCheckboxOriginalValue = this.activeRow.getBoolean("non_office_emp");
                    this.activeRow.set("non_office_emp", "Y");
                }
            }

            return true;
        };
        this.layoutTableComment.displayDispatchField = true;
        this.layoutTableComment.displayCopyOrderFields = false;
        this.textboxFuelSurchargePay.quickInfoOverrideSearchMethod = CustomerFscQuickInfo.fuelSurchargePaySearchMethod;
        Payee401kRates.setTableParentDataSource(this.layout401kRates.tablePayee401kRate, this.mainDataSource);
        this.layout401kRates.parentDataSource = this.mainDataSource;
        this.layout401kRates.textboxId.visible = false;
        this.checkboxOfficeEmployee.addChangeListener((event: ChangeEvent) => {
            if (event.userInitiatedChange) {
                if (event.newValue === true) {
                    this.dataHeader.showSaveAndClose = false;
                } else {
                    this.dataHeader.showSaveAndClose = this.showSaveAndCloseOnDataHeader;
                }
            }
        });
        this.panelDeductions.visible = !this.isMultiCurrencyEnabled;
    }

    doAfterDataHeaderSetup(dataHeader: DataHeader) {
        this.showSaveAndCloseOnDataHeader = dataHeader.showSaveAndClose;
        super.doAfterDataHeaderSetup(dataHeader);
    }

    addJobClassificationValidation(event: DataSourceValidationEvent) {
        if (this.activeRow.get("drs_payee.pay_method") == "H" || !this.activeRow.isNull("drs_payee.regular_rate") || !this.activeRow.isNull("drs_payee.vacation_pay_rate") || !this.activeRow.isNull("drs_payee.overtime_rate")) {
            if (this.activeRow.isNull("drs_payee.division_id")) {
                event.validationResults.push({
                    isValid: false,
                    component: this.textboxDrsPayeedivisionId,
                    validationMessage: this.textboxDrsPayeeexperienceDate.caption
                });
            }
            if (this.activeRow.isNull("drs_payee.terminal_id")) {
                event.validationResults.push({
                    isValid: false,
                    component: this.textboxDrsPayeeterminalId,
                    validationMessage: this.textboxDrsPayeeexperienceDate.caption
                });
            }
            if (this.activeRow.isNull("drs_payee.job_class_code_id")) {
                event.validationResults.push({
                    isValid: false,
                    component: this.textboxDrsPayeejobClassCodeId,
                    validationMessage: this.textboxDrsPayeeexperienceDate.caption
                });
            }
        }
    }

    public static navigateTo(id: string): void {
        Navigation.navigateTo(this.viewPayeeUrl + id, { newTab: true });
    }

    setupComponentListeners() {
        this.textboxTypeOf.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            // Prevent switching to or from COMPANY_DRIVER to avoid W2/1099 data issues
            if (event.hasSelection()) {
                const wasCompanyDriver = event.deselection?.value === COMPANY_DRIVER;
                const isCompanyDriver = event.selection?.value === COMPANY_DRIVER;
                const originalValue = this.activeRow.originalData?.["drs_payee.type_of"];
                if (this.mainDataSource.mode === DataSourceMode.UPDATE && originalValue != null && (wasCompanyDriver !== isCompanyDriver)) {
                    // Revert selection and show warning
                    this.textboxTypeOf.selectedItem = event.deselection;
                    Snackbar.showWarningSnackbar(
                        "Using the same payee code with the new type will cause W2 and 1099 data to be incorrect. You must setup a new payee code.",
                        { targetPanel: this }
                    );
                    return;
                }
                this.updatePayeeType(event.selection?.value);
            }
        });
        this.textboxPayMethod.addChangeListener((event: ChangeEvent) => {
            if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
                return;
            }
            this.textboxPayMethod.setSelectedItemFromDisplayValue(event.newValue);
            this.setPayMethod(this.textboxPayMethod.selectedItem?.value);
        });
        this.textboxPaymentMethod.addChangeListener((event: ChangeEvent) => {
            if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
                return;
            }
            if (this.activeRow == null) {
                return;
            }
            this.textboxPaymentMethod.setSelectedItemFromDisplayValue(event.newValue);
            const dbValue = this.textboxPaymentMethod.selectedItem?.value;

            this.setupDirectDepositTab(dbValue == DIRECT_DEPOSIT);

            if (dbValue != "D") {
                this.activeRow.set("ach_batch_class_code", null);
            }

            // Set Contract Fields
            let showContractTransferFields = false;

            if (this.activeRow.get("is_contract_transfer") == true && dbValue == "T") {
                showContractTransferFields = true;
            }

            this.panelPayToFour.visible = showContractTransferFields;

            this.textboxParentContract.visible = showContractTransferFields;
            this.textboxEfsAccount.visible = showContractTransferFields;
            this.textboxChildContract.visible = showContractTransferFields;

            this.textboxParentContract.required = showContractTransferFields;
            this.textboxEfsAccount.required = showContractTransferFields;
            this.textboxChildContract.required = showContractTransferFields;
        });
        this.switchEFTForIndividual.addChangeListener((event: ChangeEvent) => {
            this.setEFTFieldsVisibility();
        });
        this.textboxCode.addChangeListener((event: ChangeEvent) => {
            if (event.userInitiatedChange && this.mainDataSource.mode != DataSourceMode.SEARCH) {
                this.sourceDrsRecurDeduct.data.forEach(row => {
                    row.set("payee_id", event.newValue);
                });
            }
        });
    }

    setPayMethod(value: string) {
        this.switchCalcAgentPay.visible = value == "P" && this.activeRow.get("drs_payee.type_of") == AGENT;
    }

    setEFTFieldsVisibility() {
        this.textboxEftFirstName.visible = this.textboxEftMiddleInitial.visible = this.textboxEftLastName.visible = this.switchEFTForIndividual.checked;
        this.textboxEftFirstName.required = this.textboxEftLastName.required = this.switchEFTForIndividual.checked;
    }

    async validateTchChildCarrier(): Promise<boolean> {
        if (this.activeRow.get("is_contract_transfer") == true && this.activeRow.get("payment_method") == "T") {
            const response = await Api.post("lme/settlement/validate-tch-child-carrier",
                {
                    efs_acct: this.activeRow.get("drs_payee.efs_account"),
                    child_contract: this.activeRow.get("drs_payee.child_contract")
                }
            );
            if (!response?.data[0].is_valid) {
                Snackbar.showWarningSnackbar(response?.data[0]?.response);
            }

            return response?.data[0].is_valid;
        } else {
            return true;
        }
    }

    async sourceDrsPayeeAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.activeRow == null) {
            return;
        }
        this.buttonCarrierFind.visible = this.mainDataSource.mode == DataSourceMode.UPDATE && this.activeRow.get("drs_payee.type_of") == "R";
        if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
            this.setupDirectDepositTab(true);
        }
        this.setAccountingInterfaceVisible();
        this.setTitle();
        this.setEFTFieldsVisibility();
        this.setTriumphPayVisibility(event);
        this.setPaymentMethods();
        this.setupLeaveTab();
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            this.setupComponentListeners();
            this.setRequiredFields();

            if (event.newMode === DataSourceMode.ADD) {
                this.textboxStatus.setSelectedItemFromValue("A");
                if (this.textboxCurrency.visible) {
                    this.activeRow.setValueAndLookupModelData("currency", CurrencySettings.getTransactionalCurrency(), { currency_type_id: CurrencySettings.getTransactionalCurrency() });
                }
                this.textboxStatus.removeItemsWithValue("N");
            }

            if (PermissionsUtil.isUserDeniedLayout("lme/settlement/officepayroll/OfficePayee")) {
                if (event.newMode === DataSourceMode.ADD) {
                    this.checkboxOfficeEmployee.visible = false;
                    this.checkboxNonOfficeEmp.visible = false;
                } else if (event.newMode === DataSourceMode.UPDATE) {
                    this.checkboxOfficeEmployee.enabled = false;
                }
            }
        }
        this.layoutRecurringPayAdjustment.payeeDataSource = this.mainDataSource;
    }

    async setTriumphPayVisibility(event) {
        const tPActive = await this.isTriumphpayActive();
        if (tPActive) {

            if (event.newMode === DataSourceMode.UPDATE) {

                if (this.activeRow.get("payment_method") == "U") {
                    this.tabTriumphPay.visible = true;

                    this.checkboxDrsPayeetermIsQuickPay.visible = false;
                    this.labelDrsPayeeIsQuickPay.visible = true;
                    this.labelIsQuickPay.visible = true;

                    this.checkboxDrsPayeeisFactored.visible = false;
                    this.labelDrsPayeeisFactored.visible = true;
                    this.labelFact.visible = true;

                } else {
                    this.tabTriumphPay.visible = false;
                }


            } else if (event.newMode === DataSourceMode.SEARCH) {
                this.tabTriumphPay.visible = true;

                this.checkboxDrsPayeetermIsQuickPay.visible = true;
                this.labelDrsPayeeIsQuickPay.visible = false;
                this.labelIsQuickPay.visible = false;

                this.checkboxDrsPayeeisFactored.visible = true;
                this.labelDrsPayeeisFactored.visible = false;
                this.labelFact.visible = false;

            } else {
                this.tabTriumphPay.visible = false;
            }
        }
    }

    sourceDrsPayeeAfterExecution(event: DataSourceExecutionEvent) {
        if (this.activeRow == null) {
            return;
        }
        (this.layoutImageTable as ImageTable).populateForRow("P", this.mainDataSource.activeRow.get("id"));

        if (event.getAction() === DataSourceAction.SEARCH) {
            const payeeType = this.activeRow.get("drs_payee.type_of");
            this.checkIfPayeeIsBeingProcessed();
            this.updatePayeeType(payeeType).then(() => {
                this.setPayMethod(this.activeRow.get("drs_payee.pay_method"));
            });
            this.setupDirectDepositTab(this.activeRow.get("payment_method") == DIRECT_DEPOSIT);
            this.setTriumphPayVisibility(event);
            this.textboxDrsPayeeexperienceDate.required = !this.activeRow.isNull("drs_payee.pay_rate_group");
            this.setupOfficePayeeButton();
        }
        if (event.getAction() === DataSourceAction.UPDATE) {
            this.setupOfficePayeeButton();
        }
        if (this.activeRow.get("status") !== "N") {
            this.textboxStatus.removeItemsWithValue("N");
        }
        if (this.setOfficeEmployee) {
            this.openOfficePayeeScreen();
        }
        this.dataHeader.showSaveAndClose = this.showSaveAndCloseOnDataHeader;
    }

    async openOfficePayeeScreen() {
        this.setOfficeEmployee = false;
        const urlKey = this.activeRow.get("id");
        await this.officePayeeLayout.mainDataSource.search({
            id: this.activeRow.get("id"),
            ignore_office_flag: true
        }, null, this.officePayeeLayout.layoutName);
        const urlParam = { mode: "update", key: urlKey };
        const crud = new CrudDecorator({
            pseudoNavigateUrl: "lme/settlement/officepayroll/OfficePayee" + UrlUtil.buildQueryString(urlParam),
            layout: this.officePayeeLayout,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            },
            doAfterSlideIn: () => {
                this.officePayeeLayout.checkboxOffice.checked = true;
                this.officePayeeLayout.checkboxSettlement.checked = this.settlementCheckboxOriginalValue;
                this.officePayeeLayout.checkboxOffice.enabled = false;
                this.officePayeeLayout.checkboxSettlement.enabled = false;
                this.officePayeeLayout.mainDataSource.post();
            },
            doAfterSlideOut: () => {
                this.search({ id: this.activeRow.get("id") });
            },
            mode: DataSourceMode.UPDATE
        });
        crud.slideIn({ speed: 200 });
    }

    setupOfficePayeeButton() {
        this.buttonOfficePayee.visible = this.activeRow.getBoolean("office_employee", false) && !PermissionsUtil.isUserDeniedLayout("lme/settlement/officepayroll/OfficePayee");
    }

    async checkIfPayeeIsBeingProcessed() {
        const triumphPaySubmittedRecord = await Model.searchSingleRecord("lme/settlement/settlement", {
            payee_id: this.activeRow.get("id"),
            process_status: "T",
            ready_to_pay_flag: "<>V"
        }, { id: "id" });
        const carrierAgentPayee = this.activeRow.get("drs_payee.type_of") == CARRIER || this.activeRow.get("drs_payee.type_of") == AGENT;
        const hasProcessStatus = this.activeRow.get("drs_payee.process_status") != null;
        const isCheckPrinted = this.activeRow.get("drs_payee.process_status") == "C";
        if ((carrierAgentPayee && isCheckPrinted) || (!carrierAgentPayee && hasProcessStatus) || triumphPaySubmittedRecord != null) {
            Snackbar.showWarningSnackbar("Payee is in settlement payment processing. Any changes to the payee file may affect settlement totals.", { persist: true });
            this.textboxPaymentMethod.enabled = false;
            this.textboxFactoringCompanyId.enabled = false;
            this.switchPayFactoringCompany.enabled = false;
            this.textboxFreezeBy.enabled = false;
            this.switchFreezePay.enabled = false;
            return true;
        }
    }

    private setTitle() {
        if (!this.activeRow.isNull("name")) {
            this.titleEditModeSuffix = "{name}";
        } else {
            this.titleEditModeSuffix = "{check_name}";
        }
    }

    private async setPaymentMethods() {
        let paymentMethods = [];
        const tPActive = await this.isTriumphpayActive();

        if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
            const isTriumphPayLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.triumphpay");
            const isContactTransferLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.ifuel");
            paymentMethods = allPaymentMethods.filter(item => {
                if (item.value == "T") {
                    return isContactTransferLicensed;
                } else if (item.value == "U") {
                    return isTriumphPayLicensed;
                }
                return true;
            });
        } else {
            if (this.activeRow.getBoolean("drs_payee.pay_factoring_company", false)) {
                paymentMethods.push(dropdownFactoringCompany);
            }
            if (this.activeRow.get("drs_payee.type_of") == CARRIER) {
                paymentMethods.push(...basePaymentMethods);
                if (tPActive) {
                    paymentMethods.push(dropdownTriumphPay);
                }
                if (this.activeRow.get("is_contract_transfer") == true) {
                    paymentMethods.push(dropdownContractTransfer);
                }
            } else if ([AGENT, COMPANY_DRIVER, OWNER].includes(this.activeRow.get("drs_payee.type_of"))) {
                paymentMethods.push(...basePaymentMethods);
                if (this.activeRow.get("is_contract_transfer") == true) {
                    paymentMethods.push(dropdownContractTransfer);
                }
            } else {
                paymentMethods = allPaymentMethods;
            }
        }
        this.textboxPaymentMethod.items = paymentMethods;
        this.textboxPaymentMethod.selectedItem = paymentMethods.find(item => item.value == this.activeRow.get("payment_method"));
    }

    buttonCarrierFindOnClick(event: ClickEvent) {
        const urlParams = { mode: "update", key: this.activeRow.get("id") };
        const carrierURL = "lme/dispatch/Carrier" + UrlUtil.buildQueryString(urlParams);
        Navigation.navigateTo(carrierURL, { newTab: true });
    }

    textboxEarningCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    textboxPayOptionAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.userInitiatedChange && !event.hasSelection()) {
            this.activeRow.set("method", null);
            this.activeRow.set("detention_pay_rate", null);
            this.activeRow.set("detention_max_pay_amt", null);
            this.activeRow.set("earning_code", null);
        }
    }

    textboxDetentionPayRateOnBlur(event: BlurEvent) {
        this.textboxEarningCode.required = !this.activeRow.isNull("drs_payee.detention_pay_rate");
    }

    private setDirectDepositPercentageCount() {
        const [hasPercentageRow, percentageTotals] = this.layoutDirectDeposit.getDirectDepositPercentageTotal();

        if (hasPercentageRow) {
            this.labelDirectDepositStatusMessage.visible = true;
            this.labelDirectDepositStatusMessage.fontBold = true;
            this.labelDirectDepositStatusMessage.fontSize = "xxlarge";
            this.labelDirectDepositStatusMessage.color = "default";
            this.labelDirectDepositStatusMessage.text = this.isMultiCurrencyEnabled ? "Percentage total(s): " : "Percentage total: ";
            for (const [currencyType, percentage] of percentageTotals.entries()) {
                this.labelDirectDepositStatusMessage.text += this.isMultiCurrencyEnabled ? `${currencyType} ${percentage}% ` : `${percentage}% `;
            }

            if (!this.layoutDirectDeposit.percentagesValid(percentageTotals)) {
                this.labelDirectDepositStatusMessage.color = "warning";
            }
        } else {
            this.labelDirectDepositStatusMessage.visible = false;
        }
    }


    private setAccountingInterfaceVisible() {
        const isAccountingInterfaceLicensed =
            LicenseTester.testPackage("com.tms.client.loadmaster.accountinginterface");
        const isDynamicsGPLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.dynamicsgp");
        const isVmPartnerLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.tmt");
        const gpOrAccountingInterface = isAccountingInterfaceLicensed || isDynamicsGPLicensed;

        this.tabAccountingInterface.visible = gpOrAccountingInterface || isVmPartnerLicensed;
        this.textboxPayeegpImported.visible = gpOrAccountingInterface;
        this.textboxPayeegpExported.visible = gpOrAccountingInterface;
        this.switchPayeegpExclude.visible = gpOrAccountingInterface;
        this.layoutGPMapping.visible = gpOrAccountingInterface;
        this.checkboxVmPartUpdate.visible = isVmPartnerLicensed;
        this.panelContainer.visible = this.activeRow.getBoolean("is_container_enabled", false);
        this.layoutSegAllocDetail.visible = getSegAllocControlBoolean("is_active", false);
    }

    private setCarrierDefaults() {
        this.textboxPayMethod.items = carrierPayMethods;
        this.textboxPayMethod.selectedItem = dropdownFlat;
        this.textboxPayMethod.visible = true;
        this.switchPayFactoringCompany.visible = true;
        this.textboxFactoringCompanyId.visible = true;
        this.textboxLoadedPayRate.visible = true;
        this.textboxFuelSurchargePay.visible = true;
    }

    private setAgentDefaults() {
        this.textboxPayMethod.items = agentPayMethods;
        this.textboxPayMethod.selectedItem = dropdownPercentageOfRevenue;
        this.textboxPayMethod.visible = true;
        this.activeRow.set("drs_payee.pay_factoring_company", "N");
        this.activeRow.set("drs_payee.factoring_company_id", null);
        this.activeRow.set("drs_payee.fuel_surcharge_pay", null);
        this.switchPayFactoringCompany.checked = false;
        this.switchPayFactoringCompany.visible = false;
        this.textboxFactoringCompanyId.visible = false;
        this.textboxLoadedPayRate.visible = true;
        this.textboxFuelSurchargePay.visible = false;
    }

    setCompanyDriverDefaults() {
        this.textboxPayMethod.items = companyDriverPayMethods;
        this.textboxPayMethod.selectedItem = null;
        this.switchCalcAgentPay.checked = false;
        this.switchCalcAgentPay.visible = false;
        this.textboxLoadedPayRate.visible = false;
        this.switchPayFactoringCompany.visible = false;
        this.textboxFactoringCompanyId.visible = false;
    }

    setOwnerDefaults() {
        this.textboxPayMethod.items = ownerPayMethods;
        this.textboxPayMethod.selectedItem = null;
        this.textboxLoadedPayRate.visible = false;
        this.switchCalcAgentPay.visible = false;
        this.switchPayFactoringCompany.visible = false;
        this.textboxFactoringCompanyId.visible = false;
    }


    textboxFuelSurchargePayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.surcharge_type = "R";
    }

    textboxQuickpayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    textboxFactoringCompanyIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    switchPrint1099OnChange(event: ChangeEvent) {
        this.textboxPayeelegalName.required = this.switchPrint1099.checked;
    }

    switchPayeeemailSummaryOnChange(event: ChangeEvent) {
        this.textboxPayeeemail.required = this.switchPayeeemailSummary.checked;
    }

    switchQuickpayEnabledOnChange(event: ChangeEvent) {
        this.textboxQuickpayDeductId.required = this.switchQuickpayEnabled.checked;
        this.textboxQuickpayBasis.required = this.switchQuickpayEnabled.checked;
        this.textboxQuickpayDaystopay.required = this.switchQuickpayEnabled.checked;
        this.textboxQuickpayAmount.required = this.switchQuickpayEnabled.checked;
    }

    switchPayFactoringCompanyOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && event.userInitiatedChange) {
            this.textboxFactoringCompanyId.required = this.textboxFactoringCompanyId.enabled = this.switchPayFactoringCompany.checked;
            if (this.switchPayFactoringCompany.checked) {
                this.activeRow.set("payment_method", "5");
            } else {
                this.activeRow.set("payment_method", null);
                this.activeRow.set("drs_payee.factoring_company_id", null);
            }
            this.setPaymentMethods();
        }
    }

    private setRequiredFields() {
        this.switchPrint1099OnChange(null);
        this.textboxFactoringCompanyId.required = this.textboxFactoringCompanyId.enabled = this.switchPayFactoringCompany.checked;
    }

    textboxPayeenameOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_name")) {
                this.activeRow.set("check_name", this.activeRow.get("name"));
            }
            if (this.activeRow.isNull("legal_name")) {
                this.activeRow.set("legal_name", this.activeRow.get("name"));
            }
        }
    }

    textboxPayeeaddress1OnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_address")) {
                this.activeRow.set("check_address", this.activeRow.get("address1"));
            }
        }
    }

    textboxPayeeaddress2OnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_address2")) {
                this.activeRow.set("check_address2", this.activeRow.get("address2"));
            }
        }
    }

    citystatePayeeCityIdOnBlur(event: BlurEvent) {
        if (this.mainDataSource.mode == DataSourceMode.ADD) {
            if (this.activeRow.isNull("check_city")) {
                this.activeRow.set("check_city", this.activeRow.get("city"));
            }
            if (this.activeRow.isNull("check_st")) {
                this.activeRow.set("check_st", this.activeRow.get("state"));
            }
            if (this.activeRow.isNull("check_zip")) {
                this.activeRow.set("check_zip", this.activeRow.get("zip_code"));
            }
            if (this.activeRow.isNull("check_city_id")) {
                this.activeRow.set("check_city_id", this.activeRow.get("city_id"));
            }
        }
    }

    sourceDrsPayeeBeforeModeChange(event) {
        if (event.newMode === DataSourceMode.ADD) {
            this.textboxCode.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "payee" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                this.textboxCode.required = !this.codeGenerationIsActive;
                if (this.codeGenerationIsActive) {
                    this.textboxCode.caption = "*Payee Code";
                    this.textboxCode.enabledDuringAdd = this.allowManualEntry;
                }

                DUPLICATE_VALIDATION_PROPS.codeGenerationIsActive = this.codeGenerationIsActive;
                DuplicateId.configureIdTextbox(this.textboxCode, DUPLICATE_VALIDATION_PROPS);
            });
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxFactoringCompanyId. */
    textboxFactoringCompanyIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const data = event.selection;
        if (data != null) {
            const factoringFreezePay = data.getString("freeze_pay");
            if (factoringFreezePay == "Y") {
                this.textboxFreezeBy.setSelectedItemFromValue("F");
            } else if (this.activeRow?.get("freeze_pay") == "Y") {
                this.textboxFreezeBy.setSelectedItemFromValue("P");
            } else {
                this.textboxFreezeBy.setSelectedItemFromValue(null);
            }
        }

    }

    /** This is an event handler for the onChange event of switchFreezePay. */
    switchFreezePayOnChange(event: ChangeEvent) {
        const freezeBy = this.textboxFreezeBy.selectedItem?.value;
        if (!event.newValue && freezeBy == "P") {
            this.textboxFreezeBy.setSelectedItemFromValue(null);
        }

        if (event.newValue && freezeBy == null) {
            this.textboxFreezeBy.setSelectedItemFromValue("P");
        }
    }

    setupDirectDepositTab(isDirectDeposit: boolean) {
        this.textboxPayeeachBatchClassCode.visible = isDirectDeposit;
        this.layoutDirectDeposit.visible = isDirectDeposit;
        this.tabDirectDeposit.visible = isDirectDeposit;
    }

    setupGeneralTab(payeeType: string) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            this.textboxLastRaiseDate.visible = [COMPANY_DRIVER, OWNER].includes(payeeType);
            this.labelJobClassification.text = payeeType == COMPANY_DRIVER ? "Job Classification" : "Job Classification for Workers' Compensation";
            this.buttonClearClassification.enabled =
                !this.activeRow.isNull("drs_payee.division_id") ||
                !this.activeRow.isNull("drs_payee.terminal_id") ||
                !this.activeRow.isNull("drs_payee.job_class_code_id");
        }
    }

    setPayDetails(payeeType: string) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const isDriverOrOwner = [COMPANY_DRIVER, OWNER].includes(payeeType);
            this.panelSingleDriver.visible = isDriverOrOwner;
            this.panelTeamDrivers.visible = isDriverOrOwner;
            this.panelOverrides.visible = isDriverOrOwner;
            this.panelDistanceContainer.visible = isDriverOrOwner;
            this.textboxDrsPayeepayRateGroup.visible = isDriverOrOwner;
            this.textboxDrsPayeedriverRating.visible = isDriverOrOwner;
            this.textboxDrsPayeeexperienceDate.visible = isDriverOrOwner;
            this.textboxDrsPayeefuelReimburseId.visible = isDriverOrOwner;
            this.switchReimburseFuel.visible = isDriverOrOwner;

            this.panelRates.visible = payeeType == COMPANY_DRIVER;
            this.panelDeductions.visible = payeeType == OWNER;
        }
    }

    setupYTDTab() {
        if (this.activeRow.get("drs_payee.type_of") == COMPANY_DRIVER) {
            this.panelNonDriverYTD.visible = false;
            this.panelYTDWages.visible = true;
            this.panelYTDWithheld.visible = true;
            this.panelContributions.visible = true;
            this.panelSettlementHours.visible = true;
            this.panelSettlementTotalHours.visible = true;
        } else {
            this.panelNonDriverYTD.visible = true;
            this.panelYTDWages.visible = false;
            this.panelYTDWithheld.visible = false;
            this.panelContributions.visible = false;
            this.panelSettlementHours.visible = false;
            this.panelSettlementTotalHours.visible = false;
        }
    }

    setupDetentionTab(payeeType: string) {
        if (!this.isDetentionLicensed()) {
            return;
        }

        this.tabDetention.visible = getDetentionControlBoolean("is_active", false)
            && [COMPANY_DRIVER, CARRIER, OWNER].includes(payeeType);

        const isCompanyDriver = payeeType == COMPANY_DRIVER;
        this.labelDetentionSingle.visible = isCompanyDriver;
        this.labelDetentionTeam.visible = isCompanyDriver;
        this.textboxDrsPayeeteamMethod.visible = isCompanyDriver;
        this.textboxDrsPayeedetentionTmPayRate.visible = isCompanyDriver;
        this.textboxDrsPayeedetentionTmMaxPayAmt.visible = isCompanyDriver;
        this.textboxDrsPayeedetentionTmEarningCode.visible = isCompanyDriver;
        this.textboxPayOption.rowBreak = isCompanyDriver;
    }

    private isDetentionLicensed(): boolean {
        return LicenseTester.testPackage("com.tms.client.loadmaster.det");
    }

    setupLeaveTab() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.buttonAdjustLeave.visible = false;
            return;
        }

        const payeeType = this.activeRow.get("drs_payee.type_of");

        this.tabLeave.visible = payeeType == COMPANY_DRIVER;
        this.panelLeave.visible = payeeType == COMPANY_DRIVER;

        if (this.mainDataSource.mode === DataSourceMode.ADD) {
            this.buttonAdjustLeave.visible = false;
            this.layoutTableLeaveSick.enabled = false;
            this.layoutTableLeaveVacation.enabled = false;
        }

        if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
            this.buttonAdjustLeave.visible = true;
            const whenVacAccrues = this.activeRow.get("off_payee.when_vac_accrues");
            const whenSickAccrues = this.activeRow.get("off_payee.when_sick_accrues");
            this.textboxOffPayeevacationHrsBase.enabled = whenVacAccrues !== ACCRUES_BEGIN_YEAR && whenVacAccrues !== ACCRUES_ANNIVERSARY;
            this.textboxOffPayeesickHoursBase.enabled = whenSickAccrues !== ACCRUES_BEGIN_YEAR && whenSickAccrues !== ACCRUES_ANNIVERSARY;
            this.layoutTableLeaveSick.showSlideout = LeaveHistory.showSettlementAsSlideout;
        }
    }

    async setup401kRatesTab() {
        this.tab401kRates.visible = this.activeRow.get("drs_payee.type_of") === COMPANY_DRIVER;
        if (this.tab401kRates.visible) {
            if (this.hasOffice401kRecord == null && this.mainDataSource.mode === DataSourceMode.UPDATE) {
                this.hasOffice401kRecord = await Model.searchSingleRecord("lme/settlement/payee-401krate-hdr", {
                    category: "O",
                    id: this.activeRow.get("id")
                }) != null;
            }
            this.tab401kRates.visible = !this.hasOffice401kRecord;
        }
    }

    textBoxRightAlignPrintable(event: PrintableEvent, width: number) {
        if (event.printable) {
            const textbox = event.target as Textbox;
            textbox.width = width;
            textbox.align = HorizontalAlignment.RIGHT;
            textbox.captionProps = { align: HorizontalAlignment.RIGHT };
        }
    }

    textBoxLeftAlignPrintable(event: PrintableEvent, width: number) {
        if (event.printable) {
            const textbox = event.target as Textbox;
            event.printableLabel.align = HorizontalAlignment.LEFT;
            textbox.captionProps = { align: HorizontalAlignment.LEFT };
        }
    }

    textBoxLeftAlignOnPrintable(event: PrintableEvent) {
        this.textBoxLeftAlignPrintable(event, 160);
    }

    textBoxRightAlignOnPrintable(event: PrintableEvent) {
        this.textBoxRightAlignPrintable(event, 160);
    }

    textBoxRightAlignSmallOnPrintable(event: PrintableEvent) {
        this.textBoxRightAlignPrintable(event, 80);
    }

    /**
     * Configures tax-related fields based on payee type, W4 status, and tax table settings
     * Fields are shown/hidden appropriately for different tax scenarios
     */
    async setupTaxTab() {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return; // No UI adjustments needed in search mode
        }

        this.textboxStMaritalStatus.required = !this.activeRow.isNull("state_tax_code");

        const isCompanyDriver = this.activeRow.get("drs_payee.type_of") === COMPANY_DRIVER;
        this.tabTaxes.visible = isCompanyDriver;
        this.textboxFedMaritalStatus.required = isCompanyDriver && !this.activeRow.getBoolean("disable_payroll_taxes", false);
        if (!isCompanyDriver) {
            return; // No need to configure tax fields for non-driver payees
        }

        // Extract table configuration flags
        const flags = {
            useFedTaxTable: this.activeRow?.getBoolean("fed_auto_tax_table") || false,
            useStateTaxTable: this.activeRow?.getBoolean("state_auto_tax_table") || false,
            useSuiStateTaxTable: this.activeRow?.getBoolean("sui_auto_tax_table") || false,
            useCountyTaxTable: this.activeRow?.getBoolean("county_auto_tax_table") || false,
            useCityTaxTable: this.activeRow?.getBoolean("city_auto_tax_table") || false
        };

        // Determine W4 version
        const isPrior2020W4 = this.activeRow.get("w4_status") === "P";

        // Define field visibility requirements by context using direct component references
        const fieldGroups: Record<string, Map<Component, boolean>> = {
            // Pre-2020 W4 fields
            prior2020Fields: new Map<Component, boolean>([
                [this.textboxFedExemptions, true],
                [this.textboxAddFedToWh, true],
                [this.textboxFedAmtToWhold, true],
                [this.textboxTaxTableFitMisc, flags.useFedTaxTable],
                [this.textboxTaxTableFitAux, flags.useFedTaxTable]
            ]),

            // Current W4 fields (2020 and later)
            currentW4Fields: new Map<Component, boolean>([
                [this.textboxDependents, true],
                [this.textboxOtherIncome, true],
                [this.textboxOtherDeductions, true],
                [this.textboxExtraWithholdings, true],
                [this.checkboxExemptFromFed, true],
                [this.checkboxMultipleJobs, true]
            ]),


            // State tax table fields
            stateTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableStAux, flags.useStateTaxTable],
                [this.textboxTaxTableStMisc, flags.useStateTaxTable]
            ]),

            // SUI tax table fields
            suiTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableSuiAux, flags.useSuiStateTaxTable],
                [this.textboxTaxTableSuiMisc, flags.useSuiStateTaxTable]
            ]),

            // County tax table fields
            countyTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableCountyAux, flags.useCountyTaxTable],
                [this.textboxTaxTableCountyMisc, flags.useCountyTaxTable],
                [this.textboxCountyExemptions, flags.useCountyTaxTable],
                [this.textboxCountyAmtToWhold, flags.useCountyTaxTable]
            ]),

            // City tax table fields
            cityTaxFields: new Map<Component, boolean>([
                [this.textboxTaxTableCityAux, flags.useCityTaxTable],
                [this.textboxTaxTableCityMisc, flags.useCityTaxTable],
                [this.textboxCityExemptions, flags.useCityTaxTable],
                [this.textboxCityAmtToWhold, flags.useCityTaxTable]
            ])
        };

        // Hide all fields first
        this.hideTaxFields(fieldGroups);

        // Only show appropriate fields for driver payees
        if (isCompanyDriver) {
            // Apply federal tax field visibility based on W4 version
            const relevantFields = isPrior2020W4 ? fieldGroups.prior2020Fields : fieldGroups.currentW4Fields;
            this.showFieldsMatchingCondition(relevantFields);

            // Show all tax fields that are enabled by their respective flags
            [
                fieldGroups.stateTaxFields,
                fieldGroups.suiTaxFields,
                fieldGroups.countyTaxFields,
                fieldGroups.cityTaxFields
            ].forEach(fields => this.showFieldsMatchingCondition(fields));

            // Add tooltips for state, SUI, county, and city tax fields if instructions are available
            const stateInstructions = this.activeRow.get("state_instructions");
            const suiInstructions = this.activeRow.get("sui_instructions");
            const countyInstructions = this.activeRow.get("county_instructions");
            const cityInstructions = this.activeRow.get("city_instructions");

            if (flags.useStateTaxTable && stateInstructions) {
                this.textboxTaxTableStAux.tooltip = stateInstructions;
                this.textboxTaxTableStMisc.tooltip = stateInstructions;
            }

            if (flags.useSuiStateTaxTable && suiInstructions) {
                this.textboxTaxTableSuiAux.tooltip = suiInstructions;
                this.textboxTaxTableSuiMisc.tooltip = suiInstructions;
            }

            if (flags.useCountyTaxTable && countyInstructions) {
                this.textboxTaxTableCountyAux.tooltip = countyInstructions;
                this.textboxTaxTableCountyMisc.tooltip = countyInstructions;
                this.textboxCountyExemptions.tooltip = countyInstructions;
            }

            if (flags.useCityTaxTable && cityInstructions) {
                this.textboxTaxTableCityAux.tooltip = cityInstructions;
                this.textboxTaxTableCityMisc.tooltip = cityInstructions;
                this.textboxCityExemptions.tooltip = cityInstructions;
            }
        }
    }

    private showFieldsMatchingCondition(fields: Map<Component, boolean>) {
        fields.forEach((shouldShow, field) => {
            if (shouldShow && field) {
                field.visible = true;
            }
        });
    }

    private hideTaxFields(fieldGroups: Record<string, Map<Component, boolean>>) {
        // Process all field groups
        Object.values(fieldGroups).forEach(fieldGroup => {
            fieldGroup.forEach((_, field) => {
                if (field) {
                    field.visible = false;
                }
            });
        });
    }

    async getStatTaxTable(stateCode: string, taxTableType: string): Promise<ModelRow> {
        return await Model.searchSingleRecord("lme/ar/statetax", {
            id: stateCode,
            include_instructions: true,
            tax_table_type: taxTableType
        }, ["use_auto_tax_table", "instructions"]);
    }


    async updatePayeeType(payeeType: string) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }
        const currentPayMethod = this.activeRow.get("drs_payee.pay_method");
        // Quick Pay
        this.tabQuickPay.visible = payeeType == CARRIER;
        // Detention
        const isDetentionLicensed = this.isDetentionLicensed();
        this.tabDetention.visible = isDetentionLicensed && payeeType == "R";

        this.textboxDrsPayeenumDaysToPay.visible = payeeType == CARRIER;
        this.switchPrint1099.visible = payeeType != COMPANY_DRIVER;
        this.textboxBoxFor1099Total.visible = payeeType != COMPANY_DRIVER;

        if (payeeType == CARRIER) {
            this.setCarrierDefaults();
        }
        if (payeeType == AGENT) {
            this.setAgentDefaults();
        }
        if (payeeType == COMPANY_DRIVER) {
            this.setCompanyDriverDefaults();
        }
        if (payeeType == OWNER) {
            this.setOwnerDefaults();
        }
        // if pay_method exists in dropdown, keep it selected, otherwise set to null
        this.textboxPayMethod.setSelectedItemFromValue(currentPayMethod);
        this.setPayDetails(payeeType);
        this.setupGeneralTab(payeeType);
        this.setPaymentMethods();
        this.setupYTDTab();
        this.setupTaxTab();
        this.setupDetentionTab(payeeType);
        this.setupLeaveTab();
        this.setup401kRatesTab();

        this.textboxPaymentMethod.selectedItem = this.textboxPaymentMethod.items[0];
        if (this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) {
            if (payeeType == CARRIER) {
                this.activeRow.set("drs_payee.expense_glid", getPayrollControlValue("carrier_exp_glid"));
            } else if (payeeType == AGENT) {
                this.activeRow.set("drs_payee.expense_glid", getPayrollControlValue("agent_exp_glid"));
            } else if (payeeType == COMPANY_DRIVER) {
                this.activeRow.set("drs_payee.expense_glid", getPayrollControlValue("driver_exp_glid"));
            } else if (payeeType == OWNER) {
                this.activeRow.set("drs_payee.expense_glid", getPayrollControlValue("leaseop_cash_glid"));
            }
            await AutoMatchProfile.applyLookupModelData(this.textboxExpenseGlid,
                this.activeRow);
        }

        this.sourceDrsRecurDeduct.data.forEach(row => {
            row.set("drs_payee.type_of", payeeType);
        });
    }

    /** This is an event handler for the onChange event of switchW4Status. */
    switchW4StatusOnChange(event: ChangeEvent) {
        if (this.activeRow) {
            this.setupTaxTab();
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxStateTaxCode. */
    textboxStateTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "S";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxSuiTaxCode. */
    textboxSuiTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "S";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxCountyTaxCode. */
    textboxCountyTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "O";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxCityTaxCode. */
    textboxCityTaxCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "C";
    }


    /** This is an event handler for the onLookupModelSelection event of textboxStateTaxCode. */
    async textboxStateTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxTaxTableStAux, this.textboxTaxTableStMisc];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        this.handleTaxCodeSelection(
            event,
            TAX_INCOME,
            boxes
        );
        this.textboxStMaritalStatus.required = event.hasSelection();
    }

    /** This is an event handler for the onLookupModelSelection event of textboxSuiTaxCode. */
    async textboxSuiTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxTaxTableSuiMisc, this.textboxTaxTableSuiAux];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        await this.handleTaxCodeSelection(
            event,
            TAX_UNEMPLOYMENT,
            boxes
        );
        this.setFocustoFirstVisibleField(boxes);
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCountyTaxCode. */
    async textboxCountyTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxCountyExemptions, this.textboxTaxTableCountyMisc, this.textboxTaxTableCountyAux, this.textboxCountyAmtToWhold];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        await this.handleTaxCodeSelection(
            event,
            TAX_INCOME,
            boxes
        );
        this.setFocustoFirstVisibleField(boxes);
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCityTaxCode. */
    async textboxCityTaxCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const boxes = [this.textboxCityExemptions, this.textboxTaxTableCityMisc, this.textboxTaxTableCityAux, this.textboxCityAmtToWhold];
        if (!event.hasSelection()) {
            this.hideTextBoxes(boxes);
        }
        await this.handleTaxCodeSelection(
            event,
            TAX_INCOME,
            boxes
        );
        this.setFocustoFirstVisibleField(boxes);
    }

    private setFocustoFirstVisibleField(boxes: Textbox[]) {
        // Focus on the first visible textbox
        for (const box of boxes) {
            if (box.visible) {
                box.focus();
                break; // Stop after focusing the first visible box
            }
        }
    }

    private async handleTaxCodeSelection(
        event: LookupModelSelectionEvent,
        taxTableType: string,
        fieldsToUpdate: Component[]) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return; // No need to modify fields in search mode
        }

        const stateCode = event.selection?.get("id");
        if (stateCode) {
            const taxTable = await this.getStatTaxTable(stateCode, taxTableType);
            const useTaxTable = taxTable?.getBoolean("use_auto_tax_table") || false;

            // Update visibility for all provided fields
            fieldsToUpdate.forEach(field => {
                if (field) {
                    field.visible = useTaxTable;
                    if (useTaxTable) {
                        field.tooltip = !taxTable.isNull("instructions") ? taxTable.get("instructions") : null;
                    }
                }
            });
        }
    }

    private hideTextBoxes(textboxes: Textbox[]) {
        textboxes.forEach(textbox => {
            textbox.visible = false;
        });
    }

    /**
     * Updates currency fields when currency selection changes
     */
    textboxCurrencyOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!this.isMultiCurrencyEnabled) return;

        if (event.hasSelection()) {
            const targetCurrencyCode = event.selection.get("currency_type_id");

            if (this.mainDataSource.mode === DataSourceMode.ADD) {
                // For ADD mode, update currency codes without conversion
                this.updateCurrencyFieldWithNewCode(this.getFieldsToConvert(), targetCurrencyCode);
            } else if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                // For UPDATE mode, convert existing values to the new currency
                this.convertCurrencyField(this.getFieldsToConvert(), targetCurrencyCode);
            }
        }
    }

    private getFieldsToConvert(): string[] {
        return this.currencyTextBoxes.map(textbox => textbox.field);
    }

    /**
     * Updates a Currency field with a new currency code without changing the amount
     */
    private updateCurrencyFieldWithNewCode(fields: string[], currencyCode: string): void {
        const values: Currency[] = fields.map(field => this.activeRow.getCurrency(field)).filter(value => value != null);
        if (values.length === 0) return;
        fields.forEach(field => {
            const currentValue = this.activeRow.getCurrency(field);
            if (currentValue) {
                const updatedValue = new Currency({
                    amount: currentValue.getAmount(),
                    currency_code: currencyCode
                });
                this.activeRow.set(field, updatedValue);
            }
        });
    }

    /**
     * Converts a Currency field value to the specified currency code
     */
    private convertCurrencyField(fields: string[], targetCurrencyCode: string): void {
        const currencyMap = new Map<string, Currency>();
        fields.forEach(field => {
            const currencyValue = this.activeRow.getCurrency(field);
            if (currencyValue != null) {
                currencyMap.set(field, currencyValue);
            }
        });

        const values: Currency[] = Array.from(currencyMap.values());
        if (values.length === 0) return;
        CurrencyUtil.convertCurrencies(
            values,
            targetCurrencyCode
        ).then(convertedCurrencies => {
            let index = 0;
            currencyMap.forEach((_, field) => {
                this.activeRow.set(field, convertedCurrencies[index]);
                index++;
            });
        });
    }

    /** This is an event handler for the onBlur event of textboxQuickpayMinChg. */
    textboxQuickpayMinChgOnBlur(event: BlurEvent) {
        this.updateCurrencyOnBlur("drs_payee.quickpay_min_chg");
    }

    /** Updates detention maximum pay amount currency when the field loses focus */
    textboxDetentionMaxPayAmtOnBlur(event: BlurEvent) {
        this.updateCurrencyOnBlur("drs_payee.detention_max_pay_amt");
    }

    /**
     * Common handler for updating currency fields on blur events
     */
    private updateCurrencyOnBlur(fieldPath: string): void {
        if (!this.isMultiCurrencyEnabled ||
            (this.mainDataSource.mode !== DataSourceMode.ADD && this.mainDataSource.mode !== DataSourceMode.UPDATE) ||
            this.activeRow.isNull("currency")) {
            return;
        }

        this.updateCurrencyFieldWithNewCode([fieldPath], this.activeRow.get("currency"));
    }


    /** This is an event handler for the onChange event of checkboxDisablePayrollTaxes. */
    checkboxDisablePayrollTaxesOnChange(event: ChangeEvent) {
        this.textboxFedMaritalStatus.required = !event.newValue;
    }

    /** This is an event handler for the onClick event of buttonJobClassification. */
    async buttonJobClassificationOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/settlement/JobClassLookup") as AutogenLayoutJobClassLookup;
        CommonDialogs.showDialog(layout, {
            title: "Job Classification Lookup",
            okProps: {
                onClick: () => {
                    if (layout.tableJobClass.selectedRow != null) {
                        const selectedRow = layout.tableJobClass.selectedRow.data as ModelRow;
                        this.activeRow.setLookupModelData("drs_payee.job_class_code_id", selectedRow.getLookupModelData("id"));
                        this.activeRow.set("drs_payee.job_class_code_id", selectedRow.get("id"));
                        this.activeRow.setLookupModelData("drs_payee.division_id", selectedRow.getLookupModelData("division_id"));
                        this.activeRow.set("drs_payee.division_id", selectedRow.get("division_id"));
                        this.activeRow.setLookupModelData("drs_payee.terminal_id", selectedRow.getLookupModelData("terminal_id"));
                        this.activeRow.set("drs_payee.terminal_id", selectedRow.get("terminal_id"));
                        this.buttonClearClassification.enabled = true;
                    }
                }
            }
        });
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDrsPayeeearningCode. */
    textboxDrsPayeeearningCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDrsPayeedetentionTmEarningCode. */
    textboxDrsPayeedetentionTmEarningCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
    }

    /** This is an event handler for the onClick event of buttonClearClassification. */
    buttonClearClassificationOnClick(event: ClickEvent) {
        this.activeRow.setLookupModelData("drs_payee.job_class_code_id", null);
        this.activeRow.set("drs_payee.job_class_code_id", null);
        this.activeRow.setLookupModelData("drs_payee.division_id", null);
        this.activeRow.set("drs_payee.division_id", null);
        this.activeRow.setLookupModelData("drs_payee.terminal_id", null);
        this.activeRow.set("drs_payee.terminal_id", null);
        this.buttonClearClassification.enabled = false;
    }

    /** This is an event handler for the beforeExecution event of sourceLeaveVacation. */
    sourceLeaveVacationBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            event.filter.applies_to = "V";
        }
    }

    /** This is an event handler for the beforeExecution event of sourceLeaveSick. */
    sourceLeaveSickBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            event.filter.applies_to = "S";
        }
    }

    /** This is an event handler for the onClick event of buttonAdjustLeave. */
    buttonAdjustLeaveOnClick(event: ClickEvent) {
        LeaveHistory.showSettlementAsSlideout({
            leaveRow: this.activeRow,
            mode: DataSourceMode.ADD,
            onClose: (newLeave: ModelRow, balanceRow: ModelRow, postMade: boolean) => {
                if (!postMade) return; // If the leave transaction was not posted, do not refresh the leave sources
                this.sourceLeaveVacation.search(this.sourceLeaveVacation.lastSearch);
                this.sourceLeaveSick.search(this.sourceLeaveSick.lastSearch);
                const appliesTo = newLeave.get("applies_to");
                if (balanceRow) {
                    const field = appliesTo === "V" ? this.textboxOffPayeevacationHoursDue : this.textboxOffPayeesickHoursDue;
                    field.text = NumberUtil.formatDecimal(balanceRow.getNumber("amount"), "0.00");
                }

                if (newLeave.getBoolean("is_beginning_balance", false)) {
                    const formattedDate = DateUtil.formatDate(newLeave.getDate("trx_date"));
                    if (appliesTo === "V") {
                        this.textboxOffPayeevacLastAccrued.text = formattedDate;
                    } else {
                        this.textboxOffPayeesickLastAccrued.text = formattedDate;
                    }
                }
            }
        });
    }

    /** This is an event handler for the afterDropdownSelection event of textboxOffPayeewhenVacAccrues. */
    textboxOffPayeewhenVacAccruesAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            if (event.selection.value === ACCRUES_BEGIN_YEAR || event.selection.value === ACCRUES_ANNIVERSARY) {
                this.textboxOffPayeevacationHrsBase.enabled = false;
                this.textboxOffPayeevacationHrsBase.clear();
            } else {
                this.textboxOffPayeevacationHrsBase.enabled = true;
            }
        }
    }

    /** This is an event handler for the afterDropdownSelection event of textboxOffPayeewhenSickAccrues. */
    textboxOffPayeewhenSickAccruesAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            if (event.selection.value === ACCRUES_BEGIN_YEAR || event.selection.value === ACCRUES_ANNIVERSARY) {
                this.textboxOffPayeesickHoursBase.enabled = false;
                this.textboxOffPayeesickHoursBase.clear();
            } else {
                this.textboxOffPayeesickHoursBase.enabled = true;
            }
        }
    }

    async isTriumphpayActive(): Promise<boolean> {
        const isTriumphPayLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.triumphpay");
        return isTriumphPayLicensed && this.activeRow.getBoolean("is_triumphpay_active", false);
    }

    textboxDrsPayeepayRateGroupOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.textboxDrsPayeeexperienceDate.required = event.hasSelection();
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDrsPayeetieredStopId. */
    textboxDrsPayeetieredStopIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    buttonOfficePayeeOnClick(event: ClickEvent) {
        const urlParams = { mode: DataSourceMode.UPDATE, key: this.activeRow.get("id") as string };
        const offPayeeUrl = "lme/settlement/officepayroll/OfficePayee" + UrlUtil.buildQueryString(urlParams);
        Navigation.navigateTo(offPayeeUrl, { newTab: true });
    }

    textboxDepartmentOnChange(event: ChangeEvent) {
        this.textboxDepartment.text = this.textboxDepartment.text.substring(0, 3);
    }
}
